import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PopupPlacement } from '@ispui/tooltip';

import { IFormButtonUi } from './model/form-button.interface';

/**
 * Form button component
 *
 * Usage:
 * ```html
 * <isp-form-button *ngFor="let button of buttonList$ | async" [button]="button"></isp-form-button>
 * ```
 */
@Component({
  selector: 'isp-form-button',
  templateUrl: './form-button.component.html',
  styleUrls: ['./scss/form-button.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormButtonComponent implements AfterViewInit {
  /** Button metadata */
  @Input() button: IFormButtonUi;

  /** Button click event */
  @Output() readonly btnClick = new EventEmitter<IFormButtonUi>();

  /** Hint attach container selector */
  @Input() hintAttachSelector: string;

  @ViewChild('buttonElement')
  ispuiButtonElement: ElementRef<HTMLButtonElement>;

  hintPlacementOrderStringified = JSON.stringify([
    'top',
    'right',
    'bottom',
    'left',
  ] as PopupPlacement[]);

  @Input() set hintPlacementOrder(order: PopupPlacement[]) {
    this.hintPlacementOrderStringified = JSON.stringify(order);
  }

  @HostBinding('style.width') get fullwidth(): string {
    return this.button.fullWidth ? '100%' : '';
  }

  /** Hint message */
  get hint(): Observable<string> {
    return this.button.disabledSubject.pipe(
      map(disabled =>
        disabled ? this.button.hintDisabled || '' : this.button.hint || '',
      ),
    );
  }

  ngAfterViewInit(): void {
    if (this.button.autofocus) {
      // @HACK wait for ispui-button render and visible to set focus
      setTimeout(() => {
        this.ispuiButtonElement?.nativeElement.focus();
      }, 300);
    }
  }

  /**
   * Sends the click event
   */
  handleButtonClick(): void {
    const buttonDisabled =
      this.button.disabledSubject.value || this.button.preloaderSubject.value;
    if (!buttonDisabled) {
      this.btnClick.emit(this.button);
    }
  }
}
